import moment from 'moment'
import { axios, IRequestConfig, getConfigs } from './api'

export interface PsModel {
  psNumeroFacturation: string
  psIdentStructure: string
  authentificationMode: string
  codePorteurCPS?: string
}

export interface SearchTraitsInsiDTO {
  psModel: PsModel
  insTraits: {
    beneficiaireNom: string
    beneficiaireSexe: string
    beneficiaireDateNaissance: string
    beneficiaireLieu: string
    beneficiairePrenoms: string[]
    beneficiaireRang: string
    beneficiaireNumSecu: string
    assureNumSecu: string
  }
  fichierInjecte: {
    fichierBase64: string
  }
  assertions: {
    assertionPs: string
    assertionVitale: string
  }
  isApcv: boolean
  idJfse: number
}

export interface SearchVitalCardInsiDTO {
  psModel: PsModel
  insCV: {
    beneficiaireDateNaissance: string
    beneficiaireRang: string
    beneficiaireNom: string
    beneficiaireNomNaissance: string
    beneficiaireNumSecu: string
    assureNumSecu: string
    assureNomUsage: string
    assureNomNaissance: string
    numeroSerieCV: string
  }
  fichierInjecte: {
    fichierBase64: string
  }
  assertions: {
    assertionPs: string
    assertionVitale: string
  }
  isApcv: boolean
  idJfse: number
}

export interface CheckInsiDTO {
  patientId?: number
  assureNumSecu: string
  beneficiaireNom: string
  beneficiaireNomUsage: string
  beneficiairePrenom: string
  beneficiaireSexe: string
  beneficiaireLieuNaissance: string
  beneficiaireDateNaissance: string
  beneficiaireRang: string
  beneficiaireNumSecu: string
  numeroSerieCV: string
  oid: string
  insaVerifier: string
}

export interface VerifyInsiDTO {
  psModel: {
    psNumeroFacturation: string
    psIdentStructure: string
    authentificationMode: number
    codePorteurCPS: string
  }
  checkModel: CheckInsiDTO
  fichierInjecte: {
    fichierBase64: string
  }
  assertions: {
    assertionPs: string
    assertionVitale: string
    cpsBase64AuthCertificate: string
    cpsBase64SignatureCertificate: string
  }
  isApcv: boolean
  idJfse: number
}

export interface BulkVerifyInsiDTO {
  psModel: {
    psNumeroFacturation: string
    psIdentStructure: string
    authentificationMode: number
    codePorteurCPS: string
  }
  listeVerification: CheckInsiDTO[]
  fichierInjecte: {
    fichierBase64: string
  }
  assertions: {
    assertionPs: string
    assertionVitale: string
    cpsBase64AuthCertificate: string
    cpsBase64SignatureCertificate: string
  }
}

export class InsiService {
  private static endpoint = process.env.REACT_APP_INSI_SERVER
  private static apiBackendEndpoint = process.env.REACT_APP_API_URL || ''

  static async createBatchCheckRequest(
    payload: CreateInsiBatchCheckRequestDTO,
  ): Promise<InsiVerificationBatchRequestDTO> {
    return new Promise((resolve, reject) => {
      const configs: IRequestConfig = getConfigs(
        'post',
        'application/json',
        `${this.apiBackendEndpoint}/insi/batch-requests`,
        {},
      )
      configs.data = payload

      axios(configs, resolve, reject)
    })
  }

  static async updateBatchCheckRequestResult(
    batchId: string,
    payload: UpdateInsiBatchCheckRequestResultDTO,
  ): Promise<InsiVerificationBatchRequestDTO> {
    return new Promise((resolve, reject) => {
      const configs: IRequestConfig = getConfigs(
        'patch',
        'application/json',
        `${this.apiBackendEndpoint}/insi/batch-requests/${batchId}/result`,
        {},
      )
      configs.data = payload

      axios(configs, resolve, reject)
    })
  }

  static async deleteBatchCheckRequest(batchId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const configs: IRequestConfig = getConfigs(
        'delete',
        'application/json',
        `${this.apiBackendEndpoint}/insi/batch-requests/${batchId}`,
        {},
      )

      axios(
        configs,
        () => {
          resolve()
        },
        reject,
      )
    })
  }

  static async findPendingVerificationBatchRequests(): Promise<
    InsiVerificationBatchRequestDTO[]
  > {
    return new Promise((resolve, reject) => {
      const configs: IRequestConfig = getConfigs(
        'get',
        'application/json',
        `${this.apiBackendEndpoint}/insi/batch-requests/pending`,
        {},
      )

      axios(configs, resolve, reject)
    })
  }

  static async findByTraits(dto: SearchTraitsInsiDTO): Promise<InsResponseDTO> {
    const url = `${this.endpoint}/insi/RechercheAvecTraits`
    const birthDateStr = moment(dto.insTraits.beneficiaireDateNaissance).format(
      'YYYY-MM-DD',
    )

    const requestBody = {
      ...dto,
      insTraits: {
        ...dto.insTraits,
        beneficiaireDateNaissance: birthDateStr,
      },
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': 'true',
      },
      body: JSON.stringify(requestBody),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    return data
  }

  static async getBatchResult(
    dto: InsiBatchResultRequest,
  ): Promise<InsiBatchResultResponseDTO> {
    const url = `${this.endpoint}/insi/verification/lot/resultat`

    const response = await fetch(url, {
      method: 'POST',
      body: JSON.stringify(dto),
      headers: {
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': 'true',
      },
    })
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    return data
  }

  static async findByVitalCard(
    dto: SearchVitalCardInsiDTO,
  ): Promise<InsResponseDTO> {
    const url = `${this.endpoint}/insi/RechercheAvecCarteVitale`
    const birthDateStr = moment(dto.insCV.beneficiaireDateNaissance).format(
      'YYYY-MM-DD',
    )

    const requestBody = {
      ...dto,
      insCV: {
        ...dto.insCV,
        beneficiaireDateNaissance: birthDateStr,
      },
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': 'true',
      },
      body: JSON.stringify(requestBody),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    return data
  }

  static async verifyIns(
    dto: VerifyInsiDTO,
  ): Promise<InsVerificationResponseDTO> {
    const url = `${this.endpoint}/insi/verification`
    const birthDateStr = moment(
      dto.checkModel.beneficiaireDateNaissance,
    ).format('YYYY-MM-DD')

    const requestBody = {
      ...dto,
      checkModel: {
        ...dto.checkModel,
        beneficiaireDateNaissance: birthDateStr,
      },
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': 'true',
      },
      body: JSON.stringify(requestBody),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    return data
  }

  static async bulkVerifyIns(
    dto: BulkVerifyInsiDTO,
  ): Promise<InsiBatchResponse> {
    const url = `${this.endpoint}/insi/verification/lot/depot`
    const formattedList = dto.listeVerification.map((check) => ({
      ...check,
      beneficiaireDateNaissance: moment(check.beneficiaireDateNaissance).format(
        'YYYY-MM-DD',
      ),
    }))

    const requestBody = {
      ...dto,
      listeVerification: formattedList,
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': 'true',
      },
      body: JSON.stringify(requestBody),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data: InsiBatchResponse = await response.json()
    return data
  }

  static async savePatientInsHistory(
    patientId: number,
    insHistory: PatientInsHistory,
  ): Promise<PatientInsHistory> {
    const url = `${this.apiBackendEndpoint}/billing/database/patient/ins/${patientId}`

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(insHistory),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    return data
  }

  static async getPatientInsHistory(
    patientId: number,
  ): Promise<PatientInsHistory> {
    const url = `${this.apiBackendEndpoint}/billing/database/patient/ins/${patientId}`

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    return data
  }
}

export const mapInsiResponseToHistory = (
  insiResponse: InsResponseDTO,
  patientId: number,
): PatientInsHistory => {
  const insResult = insiResponse.message
  const dateFin = insResult.historique?.[0]?.dateFin || ''

  debugger
  return {
    patient_id: patientId,
    ins_value: `${insResult.ins}${insResult.cleIns ?? ''}`,
    ins_oid: insResult.oidIns,
    insHistory: insResult.historique || [],
    enable: true,
    creation_date: new Date().toISOString().split('T')[0],
    end_date: dateFin ? moment(dateFin).format('YYYY-MM-DD') : '',
  }
}

export interface InsiBatchResultRequest {
  psModel: PsModel
  lot: {
    idLot: string
  }
  fichierInjecte: {
    fichierBase64: string
  }
  assertions: {
    assertionPs: string
    assertionVitale: string
    cpsBase64AuthCertificate: string
    cpsBase64SignatureCertificate: string
  }
}

export interface PatientInsHistory {
  id?: number
  patient_id: number
  ins_value: string
  ins_oid: string
  enable: boolean
  creation_date: string
  end_date: string
  insHistory: any[]
}

export interface InsResponseDTO {
  message: {
    reponse: {
      showQueries: boolean
      xmlQuery: string
      xmlResponse: string
      erreur: boolean
      erreurCode: string | null
      erreurMessage: string | null
      messageId: string | null
      severite: string | null
      erreurMessageComplet: string | null
    }
    ins: string
    cleIns: string
    oidIns: string
    nomNaissance: string
    prenom: string | null
    listePrenoms: string
    sexe: string
    dateNaissance: string
    lieuNaissance: string
    appelCv: boolean
    codeRetour: string
    historique: any | null
  }
}

export interface InsVerificationResponseDTO {
  message: {
    reponse: {
      showQueries: boolean
      xmlQuery: string | null
      xmlResponse: string | null
      erreur: boolean
      erreurCode: string | null
      erreurMessage: string | null
      messageId: string | null
      severite: string | null
      erreurMessageComplet: string | null
    }
    codeRetour: string
    messageId: string | null
  }
}

export interface CreateInsiBatchCheckRequestDTO {
  batchId: string
  delay: number
  dateToStartResultCheck: string
  codeReturned: string
  submittedList: (CheckInsiDTO & { patientId: number })[]
}

export interface InsiResultDTO {
  insValue: string
  insCle: string
  oid: string
  erreur: boolean
  erreurMessage: string
  dateHeureRetour: string
}

export interface InsiBatchResponse {
  message: {
    reponse: {
      showQueries: boolean
      xmlQuery: string
      xmlResponse: string
      erreur: boolean
      erreurCode: string | null
      erreurMessage: string | null
      messageId: string
      severite: string
      erreurMessageComplet: string
    }
    codeRetour: string
    horodatageFin: string
    delai: string
    lot: string
  }
}

export interface InsiBatchMessageDTO {
  reponse: any
  codeRetour: string
  delai: string
  resultats: InsiResultDTO[]
}

export interface InsiBatchResultResponseDTO {
  message: InsiBatchMessageDTO
}

export interface UpdateInsiBatchCheckRequestResultDTO {
  result?: InsiBatchResultResponseDTO
  delay?: number
}

export interface InsiVerificationBatchRequestDTO {
  batchId: string
  delay: number
  dateToStartResultCheck: string
  codeReturned: string
  submittedList: (CheckInsiDTO & { patientId: number })[]
  createdAt: string
  result?: InsiBatchResultResponseDTO
}
